import { Link } from "react-router-dom";
import { ArrowUpRight, ListChecks, Video, Sparkles, PenSquare } from "lucide-react";
import { Button } from "./ui/button";
import StartSessionPrompt from "./StartSessionPrompt";

const STEPS = [
  {
    numeral: "I",
    icon: ListChecks,
    title: "Set the agenda",
    body: "Wins, Losses, Goals, Challenges, Resolutions. The classic board format — with your name at the top.",
  },
  {
    numeral: "II",
    icon: Video,
    title: "Call the session to order",
    body: "Solo, or with your partner over live video. Record the whole thing — audio, screen, both.",
  },
  {
    numeral: "III",
    icon: Sparkles,
    title: "AI drafts your minutes",
    body: "Whisper transcribes what was said. Claude turns it into structured minutes. You review and publish.",
  },
  {
    numeral: "IV",
    icon: PenSquare,
    title: "Sign off & carry forward",
    body: "Every resolution is dated and signed. Next week's session starts with last week's promises.",
  },
];

/**
 * "See how it works" landing block — shown on Dashboard for users with zero
 * completed sessions. Converts share-clickers and new visitors into first-session
 * bookings by making the ritual legible in 15 seconds.
 */
export default function HowItWorksSection() {
  return (
    <section
      className="my-12 sm:my-16"
      data-testid="how-it-works-section"
      aria-label="How Boardroom works"
    >
      <div className="mb-8">
        <p className="text-[11px] tracking-[3px] uppercase text-[#c89f45] font-medium mb-2">
          See how it works
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#fafafa] max-w-2xl leading-tight">
          Four moves. One weekly ritual. A year of receipts.
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={step.numeral}
              className="group relative bg-[#121212] border border-[#1a1a1a] rounded-lg p-5 hover:border-[#c89f45]/50 transition-all duration-300 hover:-translate-y-0.5"
              data-testid={`how-step-${i + 1}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-serif text-2xl text-[#c89f45] leading-none">{step.numeral}</span>
                <Icon className="w-4 h-4 text-[#8a8a8a] group-hover:text-[#c89f45] transition-colors" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-lg text-[#fafafa] mb-2 leading-snug">{step.title}</h3>
              <p className="text-sm text-[#aaa] leading-relaxed">{step.body}</p>

              {/* Subtle gold accent line at the bottom on hover */}
              <div className="absolute bottom-0 left-5 right-5 h-px bg-gradient-to-r from-transparent via-[#c89f45] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <StartSessionPrompt
          testId="how-cta-first-session"
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-11 px-5 font-medium"
        >
          Call your first session
          <ArrowUpRight size={16} className="ml-2" />
        </StartSessionPrompt>
        <Link to="/sample" data-testid="how-cta-see-sample">
          <Button
            variant="outline"
            className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md h-11 px-5 font-medium"
          >
            See a sample session
            <ArrowUpRight size={16} className="ml-2" />
          </Button>
        </Link>
        <span className="text-xs text-[#8a8a8a] ml-auto">Takes ~15 minutes. Free.</span>
      </div>
    </section>
  );
}
